import type { ClientMessage, ErrorCode, Role, RoomView, ServerMessage } from '@chipless/shared';
import { WS_URL } from './env.js';

/**
 * WebSocket の接続と再接続（design 7.1）。
 *
 * API Gateway には引き上げ不可の上限がある（最大接続時間2時間、アイドルタイムアウト
 * 10分）。数時間続くセッションでは強制切断が必ず起きるため、再接続は障害対応ではなく
 * 通常の動作経路として扱う。
 */

export type ConnectionStatus =
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed'
  | 'rejected'
  /** ルームが削除された。再接続しない。 */
  | 'roomClosed';

export type ConnectionParams = {
  roomCode: string;
  token: string;
  role: Role;
};

export type ConnectionHandlers = {
  onState: (view: RoomView) => void;
  onError: (error: { code: ErrorCode; message: string }) => void;
  onStatus: (status: ConnectionStatus) => void;
};

/** アイドルタイムアウトは10分。これを下回る間隔で通信を発生させる。 */
const PING_INTERVAL_MS = 4 * 60 * 1000;
const BACKOFF_INITIAL_MS = 200;
const BACKOFF_MAX_MS = 5000;

/** オーソライザに拒否されたことを示すクローズコード（ローカルサーバと合わせる）。 */
const CLOSE_FORBIDDEN = 4403;
const CLOSE_ROOM_NOT_FOUND = 4404;

export class Connection {
  private socket: WebSocket | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private backoff = BACKOFF_INITIAL_MS;
  private stopped = false;
  private everOpened = false;

  constructor(
    private readonly params: ConnectionParams,
    private readonly handlers: ConnectionHandlers,
  ) {}

  start(): void {
    this.stopped = false;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    this.handlers.onStatus('closed');
  }

  get isOpen(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  send(message: ClientMessage): boolean {
    if (!this.isOpen) return false;
    this.socket?.send(JSON.stringify(message));
    return true;
  }

  private url(): string {
    const params = new URLSearchParams({
      room: this.params.roomCode,
      token: this.params.token,
      role: this.params.role,
    });
    return `${WS_URL}?${params.toString()}`;
  }

  private open(): void {
    this.handlers.onStatus(this.everOpened ? 'reconnecting' : 'connecting');

    const socket = new WebSocket(this.url());
    this.socket = socket;

    socket.addEventListener('open', () => {
      if (this.stopped) return;
      this.everOpened = true;
      this.backoff = BACKOFF_INITIAL_MS;
      this.handlers.onStatus('open');
      // 状態は DynamoDB にあるので、取り直すだけで何も失われない。要件2.3 の席復帰も
      // この同じ経路を通る。
      this.send({ type: 'hello' });
      this.startPing();
    });

    socket.addEventListener('message', (event: MessageEvent<string>) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data) as ServerMessage;
      } catch {
        return;
      }
      if (message.type === 'state') this.handlers.onState(message.view);
      else if (message.type === 'error') {
        this.handlers.onError({ code: message.code, message: message.message });
      } else if (message.type === 'closed') {
        // ルームが削除された。再接続するとオーソライザに拒否されるだけで、「削除された」と
        // 「見つからない」の区別が失われる。ここで打ち切る。
        this.stopped = true;
        this.clearTimers();
        const closing = this.socket;
        this.socket = null;
        closing?.close();
        this.handlers.onStatus('roomClosed');
      }
      // pong は状態を持たないので何もしない。
    });

    socket.addEventListener('close', (event: CloseEvent) => {
      this.stopPing();
      if (this.stopped) return;

      if (event.code === CLOSE_FORBIDDEN || event.code === CLOSE_ROOM_NOT_FOUND) {
        // ルームが存在しない、または不正な接続。再試行しても状況は変わらない。
        this.handlers.onStatus('rejected');
        return;
      }

      this.scheduleRetry();
    });

    socket.addEventListener('error', () => {
      // close が続けて発火するため、ここでは再接続を仕掛けない。
    });
  }

  /** 指数バックオフ。初回200ms、上限5秒。 */
  private scheduleRetry(): void {
    this.handlers.onStatus('reconnecting');
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, BACKOFF_MAX_MS);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.stopped) this.open();
    }, delay);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.send({ type: 'ping' });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private clearTimers(): void {
    this.stopPing();
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }
}
